<template>
  <div class="group-signup-container" v-if="loaded">
    <div class="headline">
      <h3 v-if="allowRegistration || allowGroupChange">
        <template v-if="eventData.registrationMethod === 'GROUPS'"
          >Group signup</template
        ><template v-else>Flex start</template>
      </h3>
      <h3 v-else>Groups</h3>
    </div>
    <div class="reserved-spot-banner" v-if="userHasReservedSpot">
      <Warning />
      <p>
        Your spot is not confirmed yet. To complete registration press your spot
        again.
      </p>
    </div>

    <div
      class="min-groupsize-info-banner"
      v-if="eventData.registrationMethod === 'FLEX'"
    >
      <Tip />
      <p>
        <b>Good to know:</b> You’ll need at least
        <b
          >{{
            formattedMinGroupSize(isSingles, eventData.minGroupStartSize)
          }} </b
        >in total in your group to tee off and unlock your scorecard.
      </p>
    </div>
    <p
      class="round-nav"
      :class="{ pools: roundToShow.pools.length > 1 }"
      v-if="roundNumber && !allowRegistration"
    >
      <b>Round {{ roundNumber }}</b>
    </p>
    <div class="pool-nav" v-if="roundToShow.pools.length > 1">
      <p
        :class="{
          active: pool.id === poolData?.id,
          disabled: pool.areGroupsPublic === false,
        }"
        v-for="(pool, index) in roundToShow.pools"
        @click="pool.areGroupsPublic ? changePool(pool.id) : null"
      >
        <b v-if="pool.name">{{ pool.name }}</b>
        <b v-else>Pool {{ poolLetters[index] }}</b>
      </p>
    </div>

    <p v-if="userInPool && poolData.status === 'CLOSED'" class="switch-copy">
      You can switch to another group if you’d like to change.
    </p>

    <template v-else-if="allowRegistration">
      <p class="registration-info" v-if="availableSpots > 0">
        Pick an available spot.
        <span
          class="spots-label"
          :class="{ few: availableSpots <= 10 }"
          v-if="eventData.maxSpots"
          ><b>{{ availableSpots }} Spots left</b></span
        >
      </p>
      <p class="registration-info" v-else>
        <span class="spots-label none"><b>No spots left</b></span>
      </p>
    </template>

    <div class="pool" v-if="poolData">
      <div
        class="group-container"
        :class="{ doubles: !isSingles }"
        v-for="(group, groupIndex) in poolData.groups"
        :key="groupIndex"
      >
        <div
          class="group-header"
          :class="{
            live:
              group.status === 'OPEN' &&
              eventData.registrationMethod === 'FLEX',
          }"
        >
          <span>
            <Basket />
            <p>
              <b>{{ group.startHole.name || group.startHole.number }}</b>
            </p>
          </span>
          <span class="start-time" v-if="group.startsAt != null">
            <Times color="white" />
            <p>
              <b>{{ dateFormatter(group.startsAt, 'HH:mm') }}</b>
            </p>
          </span>
        </div>

        <div
          class="player"
          v-for="player in group.playerConnectionsV2"
          :key="player.id"
        >
          <template v-if="player.player">
            <div class="left">
              <div class="player-wrapper">
                <UserProfileAvatar
                  size="L"
                  :profileImageUrl="player.player.user.profile.profileImageUrl"
                  :initials="
                    player.player.user.firstName.charAt(0) +
                    player.player.user.lastName.charAt(0)
                  "
                />
                <div class="player-name">
                  <p class="first">
                    <b>{{ player.player.user.firstName }}</b>
                  </p>
                  <p class="last">
                    {{ player.player.user.lastName }}
                  </p>
                </div>
              </div>
            </div>
            <div class="right">
              <p class="division-label">
                <b>
                  {{ player.player.division.type }}
                </b>
              </p>
            </div>
          </template>
          <template v-else>
            <div class="left">
              <div
                class="team-name"
                v-if="
                  player.teamPlayer.team.teamPlayers.length > 2 ||
                  eventData.defaultToTeamName
                "
              >
                <p>{{ player.teamPlayer.team.name }}</p>
              </div>
              <div class="team-wrapper" v-else>
                <div
                  class="team-player"
                  v-for="teamPlayer in player.teamPlayer.team.teamPlayers"
                >
                  <UserProfileAvatar
                    size="L"
                    :profileImageUrl="teamPlayer.user.profile.profileImageUrl"
                    :initials="
                      teamPlayer.user.firstName.charAt(0) +
                      teamPlayer.user.lastName.charAt(0)
                    "
                  />
                  <div class="player-name">
                    <p class="first">
                      <b>{{ teamPlayer.user.firstName }}</b>
                    </p>
                    <p class="last">
                      {{ teamPlayer.user.lastName }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="right">
              <p class="division-label">
                <b>
                  {{ player.teamPlayer.division.type }}
                </b>
              </p>
            </div>
          </template>
        </div>
        <div
          class="player reserved"
          :class="{
            pointer: player.user.id === userInfo.sessionInfo.userId,
          }"
          v-for="player in reservedSpotsInGroup(group)"
          @click="
            player.user.id === userInfo.sessionInfo.userId
              ? reserveSpot(group)
              : null
          "
          :key="player.id"
        >
          <div class="left">
            <div class="player-wrapper">
              <UserProfileAvatar
                size="L"
                :profileImageUrl="player.user.profile.profileImageUrl"
                :initials="
                  player.user.firstName.charAt(0) +
                  player.user.lastName.charAt(0)
                "
              />
              <div class="player-name">
                <p class="first">
                  <b>{{ player.user.firstName }}</b>
                </p>
                <p class="last">
                  {{ player.user.lastName }}
                </p>
              </div>
            </div>
          </div>
          <div class="right" @click.stop>
            <p class="reserved-label">
              <b>Reserved</b>
            </p>
          </div>
        </div>
        <div
          class="player"
          @click="spotIsAvailable(group, index) ? reserveSpot(group) : null"
          :class="{
            available: spotIsAvailable(group, index),
            disabled:
              group.status !== 'CLOSED' ||
              !allowRegistration ||
              index !== 0 ||
              usersGroup?.id === group.id,
          }"
          v-for="(emptySpot, index) in emptySpotsInGroup(group)"
          :key="index"
        >
          <AddSimplified v-if="spotIsAvailable(group, index)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Basket from '@/assets/icons/Basket'
import Times from '@/assets/icons/Time'
import AddSimplified from '@/assets/icons/AddSimplified.vue'
import dateFormats from '@/mixins/dateFormats.js'
import UserProfileAvatar from '@/components/UIElements/UserProfileAvatar'
import Warning from '@/assets/icons/Warning'
import Tip from '@/assets/icons/Tip.vue'

export default {
  name: 'GroupRegistration',
  props: {
    allowGroupChange: {
      type: Boolean,
      default: false,
    },
    allowRegistration: {
      type: Boolean,
      default: false,
    },
    roundId: {
      type: String,
      required: true,
    },
    eventId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      eventData: null,
      poolData: null,
      players: [],
      teams: [],
      loaded: false,
      groupRegistrationReservations: [],
      userTourpasses: [],
      clickedGroup: null,
      noPoolsAvailableForRegistration: true,
      eventHasStarted: false,
      showRegistrationMessageInput: false,
      userRegistrationMessage: '',
      tourPassGroup: null,
      myTeams: [],
      showChangeGroupPopup: false,
      showRegQuestionsPopup: false,
      poolId: null,
      userRegistration: null,
    }
  },
  mixins: [dateFormats],
  components: {
    Basket,
    Times,
    AddSimplified,
    UserProfileAvatar,
    Warning,
    Tip,
  },
  watch: {},
  computed: {
    isSingles() {
      return this.eventData.scoringAs === 'SINGLES'
    },
    roundNumber() {
      return this.eventData.rounds.findIndex((r) => r.id === this.roundId) + 1
    },
    roundToShow() {
      return this.eventData.rounds.find((r) => r.id === this.roundId)
    },
    userRegistrationStatus() {
      const eventRegistrations = this.eventData.registrations
      const players = this.eventData.players
      const userId = this.userInfo.sessionInfo.userId

      let userRegistrationStatus = {
        registered: false,
        registrationId: null,
        status: null,
        playerId: null,
      }

      if (this.isUserLoggedIn && this.isSingles) {
        eventRegistrations.forEach((reg) => {
          if (reg.user.id == userId) {
            userRegistrationStatus.status = reg.status
            userRegistrationStatus.registrationId = reg.id

            if (reg.status == 'PENDING' || reg.status == 'ACCEPTED') {
              userRegistrationStatus.registered = true
            }
          }
        })

        players.forEach((player) => {
          if (player.userId == userId) {
            userRegistrationStatus.playerId = player.id
          }
        })
      } else if (this.isUserLoggedIn && this.isSingles === false) {
        const userRegistrations = eventRegistrations.filter((r) =>
          this.myTeams.some((t) => t.id == r.team.id)
        )

        const registration = userRegistrations.find(
          (x) => x.status == 'PENDING' || x.status == 'ACCEPTED'
        )

        if (registration) {
          userRegistrationStatus.registered = true
          userRegistrationStatus.registrationId = registration.id
          userRegistrationStatus.status = registration.status
          userRegistrationStatus.playerId = registration.team.id
        }
      }

      return userRegistrationStatus
    },
    userInfo() {
      return this.$store.getters.userInfo
    },
    waitingListLength() {
      const registrations = this.eventData.registrations
      let waitingListLength = 0

      waitingListLength = registrations.filter(
        (registration) => registration.status == 'PENDING'
      ).length

      return waitingListLength
    },
    wildCardsLeft() {
      const players = this.eventData.players
      const maxWildcards = this.eventData.maxWildcards
      let wildCardsLeft = 0
      let wildCardOunter = 0

      players.forEach((player) => {
        if (player.isWildcard) {
          wildCardOunter++
        }
      })

      if (maxWildcards - wildCardOunter > 0) {
        wildCardsLeft = maxWildcards - wildCardOunter
      }

      return wildCardsLeft
    },
    poolLetters() {
      return this.$store.state.poolConversion
    },
    isUserLoggedIn() {
      return this.$store.getters.isUserLoggedIn
    },
    availableSpots() {
      if (!this.eventData.maxSpots) {
        return -1
      }

      return (
        this.eventData.maxSpots -
        (this.groupRegistrationReservations.length +
          this.eventData.playersCount)
      )
    },
    takenSpots() {
      return (
        this.groupRegistrationReservations.length + this.eventData.playersCount
      )
    },
    usersReservedGroup() {
      return this.groupRegistrationReservations.find(
        (reservation) => reservation.user.id == this.userInfo.sessionInfo.userId
      )?.group
    },
    userInPool() {
      return this.usersGroup
        ? this.poolData.id === this.usersGroup.pool.id
        : false
    },
    usersGroup() {
      const userId = this.$store.getters.userInfo.sessionInfo.userId
      let usersGroup = null

      if (!this.poolData) return null

      if (this.isSingles) {
        this.poolData.groups.forEach((group) => {
          group.playerConnectionsV2.forEach((player) => {
            if (player.player.userId == userId) {
              usersGroup = group
            }
          })
        })
      } else {
        this.poolData.groups.forEach((group) => {
          group.playerConnectionsV2.forEach((player) => {
            if (
              player.teamPlayer.team.teamPlayers.some(
                (tp) => tp.user.id == userId
              )
            ) {
              usersGroup = group
            }
          })
        })
      }

      return usersGroup
    },
    userHasValidTourPass() {
      const tourPasses = this.$store.getters.tourPasses
      const tourId = this.eventData.tour.id

      return (
        tourPasses.find(
          (pass) => pass.tour.id == tourId && pass.payment.isRefunded == false
        ) || null
      )
    },
    userHasReservedSpot() {
      let userId = this.$store.getters.userInfo.sessionInfo.userId
      let bool = false

      this.groupRegistrationReservations.forEach((reservation) => {
        if (reservation.user.id == userId) bool = true
      })

      return bool
    },
  },
  methods: {
    spotIsAvailable(group, index) {
      if (
        this.eventData.registrationMethod === 'FLEX' &&
        !this.allowRegistration
      ) {
        if (
          group.status === 'CLOSED' &&
          index === 0 &&
          this.usersGroup?.id !== group.id
        ) {
          return true
        }
      } else {
        if (
          this.allowRegistration &&
          index === 0 &&
          this.usersGroup?.id !== group.id &&
          group.status === 'CLOSED'
        ) {
          return true
        }

        if (
          this.allowGroupChange &&
          this.poolData.status === 'CLOSED' &&
          this.userInPool &&
          index === 0 &&
          this.usersGroup?.id !== group.id
        ) {
          return true
        }
      }

      return false
    },
    formattedMinGroupSize(singles, size) {
      if (singles) {
        return size === 1 ? '1 player' : `${size} players`
      }
      return size === 1 ? '1 team' : `${size} teams`
    },
    changePool(poolId) {
      this.loadPool(poolId)
    },
    holeDisplayName(hole) {
      if (hole.name != null) {
        return hole.name
      } else {
        return hole.number
      }
    },
    formattedGroupStartTime(time) {
      return moment(time).format('HH:mm')
    },
    formattedRegistrationOpenDate(dateTime) {
      return moment(dateTime).format('D MMMM YYYY [at] HH:mm')
    },
    reservedSpotsInGroup(group) {
      return this.groupRegistrationReservations.filter(
        (player) => player.group.id == group.id
      )
    },
    async changeGroup(newGroup) {
      let changedGroup

      try {
        if (this.isSingles) {
          changedGroup = await this.$axios({
            headers: {
              Authorization: this.$store.state.user.sessionInfo.token,
            },
            data: {
              query: `
              mutation UpdateRegistration($UpdateRegistrationInput: UpdateRegistrationInput!){
              	UpdateRegistration(registrationId:"${this.userRegistrationStatus.registrationId}",input:$UpdateRegistrationInput,asRole:USER){
                  id
                }
              }
              `,
              variables: {
                UpdateRegistrationInput: {
                  groupId: newGroup.id,
                },
              },
            },
          })
        } else {
          changedGroup = await this.$axios({
            headers: {
              Authorization: this.$store.state.user.sessionInfo.token,
            },
            data: {
              query: `
              mutation UpdateTeamRegistration($UpdateRegistrationInput: UpdateTeamRegistrationInput!){
              	UpdateTeamRegistration(registrationId:"${this.userRegistrationStatus.registrationId}",input:$UpdateRegistrationInput, asRole:USER){
                  id
                }
              }
              `,
              variables: {
                UpdateRegistrationInput: {
                  groupId: newGroup.id,
                },
              },
            },
          })
        }

        if (changedGroup.data.errors) {
          throw changedGroup.data.errors[0]
        }
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Group changed',
          type: 'success',
        })
        if (typeof window.ReactNativeWebView?.postMessage === 'function') {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              type: 'REGISTRATION_UPDATE',
            })
          )
        }
      } catch (err) {
        console.log(err)

        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Error changing group',
          type: 'error',
        })
      } finally {
        this.showChangeGroupPopup = false
        this.$emit('close')
        this.loadData()
      }
    },
    sendToLogin() {
      this.$store.dispatch('setReturnToRouteData', this.$route)
      this.$router.push({
        name: 'login',
      })
    },
    async createSpotReservation(group) {
      try {
        let registrationReservation = await this.$axios({
          headers: {
            Authorization: this.$store.state.user.sessionInfo.token,
          },
          data: {
            query: `
              mutation{
              	CreateGroupRegistrationReservation(eventId:"${this.eventId}",groupId:"${group.id}"){
                  id
                }
              }
              `,
          },
        })

        if (registrationReservation.data.errors) {
          if (
            registrationReservation.data.errors[0].errorCode ==
            'NO_AVAILABLE_SPOT_IN_GROUP'
          ) {
            this.$store.dispatch('showMessage', {
              show: true,
              message: 'No spot available',
              type: 'error',
            })
          } else if (
            registrationReservation.data.errors[0].errorCode ==
            'GROUP_NOT_CLOSED'
          ) {
            this.$store.dispatch('showMessage', {
              show: true,
              message: 'Group has already started',
              type: 'error',
            })
          } else {
            this.$store.dispatch('showMessage', {
              show: true,
              message: 'Error',
              type: 'error',
            })
          }
          this.loadData()
        } else {
          this.$emit('groupSelected', group)
        }
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Error',
          type: 'error',
        })
      }
    },
    async removePlayerFromGroup(playerId, groupId) {
      try {
        let removePlayer = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
          mutation {
            RemovePlayerFromGroup(groupId:"${groupId}", playerId:"${playerId}", asRole:USER){
              id
            }
          }
          `,
          },
        })
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Error',
          type: 'error',
        })
      }
    },
    async addPlayerToGroup(playerId, targetGroupId) {
      try {
        const addPlayer = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
            mutation {
              AddPlayerToGroup(groupId:"${targetGroupId}", playerId:"${playerId}", asRole:USER){
                id
              }
            }
          `,
          },
        })

        if (addPlayer.data.errors) {
          throw addPlayer.data.errors[0]
        }

        if (typeof window.ReactNativeWebView?.postMessage === 'function') {
          await window.ReactNativeWebView.postMessage(
            JSON.stringify({
              type: 'CLOSE_AND_REFRESH_EVENTS',
            })
          )
        } else {
          this.$store.dispatch('showMessage', {
            show: true,
            message: 'Joined group',
            type: 'success',
          })
          this.$emit('closeAndReload')
        }
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Error',
          type: 'error',
        })
        this.loadData()
      }
    },
    async addTeamToGroup(playerId, targetGroupId, refresh) {
      try {
        const addPlayer = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
            mutation {
              AddTeamPlayerToGroup(groupId:"${targetGroupId}", playerId:"${playerId}", asRole:USER){
                id
              }
            }
          `,
          },
        })
        if (typeof window.ReactNativeWebView?.postMessage === 'function') {
          await window.ReactNativeWebView.postMessage(
            JSON.stringify({
              type: 'CLOSE_AND_REFRESH_EVENTS',
            })
          )
        } else {
          this.$store.dispatch('showMessage', {
            show: true,
            message: 'Joined group',
            type: 'success',
          })
          this.$emit('closeAndReload')
        }
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Error',
          type: 'error',
        })
      }
    },
    async removeTeamFromGroup(teamPlayerId, groupId) {
      try {
        const removeTeam = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
              mutation {
                RemoveTeamPlayerFromGroup(groupId:"${groupId}", playerId:"${teamPlayerId}", asRole:USER){
                  id
                }
              }
            `,
          },
        })
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Error',
          type: 'error',
        })
      }
    },
    async reserveSpot(group) {
      const groupReservations = this.groupRegistrationReservations.filter(
        (reservation) => reservation.group.id == group.id
      )
      const userHasReservationInGroup = groupReservations.some(
        (r) => r.user.id === this.userInfo.sessionInfo.userId
      )

      if (
        this.eventData.registrationMethod === 'FLEX' &&
        this.roundNumber > 1
      ) {
        if (this.isSingles) {
          if (this.usersGroup) {
            await this.removePlayerFromGroup(
              this.userRegistration.player.id,
              this.usersGroup.id
            )
            await this.addPlayerToGroup(
              this.userRegistration.player.id,
              group.id
            )
          } else {
            await this.addPlayerToGroup(
              this.userRegistration.player.id,
              group.id
            )
          }

          return
        } else {
          const teamPlayerId = this.userRegistration.find(
            (r) => r.status === 'ACCEPTED'
          )?.player?.id

          if (this.usersGroup) {
            await this.removeTeamFromGroup(teamPlayerId, this.usersGroup.id)
            await this.addTeamToGroup(teamPlayerId, group.id)
          } else {
            await this.addTeamToGroup(teamPlayerId, group.id)
          }
          return
        }
      } else if (
        this.eventData.status == 'CLOSED' ||
        this.eventData.registrationMethod === 'FLEX'
      ) {
        if (!this.isUserLoggedIn) {
          this.sendToLogin()
        } else if (
          (this.userRegistrationStatus.registered == false &&
            this.availableSpots > 0) ||
          userHasReservationInGroup
        ) {
          if (userHasReservationInGroup) {
            this.$emit('groupSelected', group)
          } else {
            this.createSpotReservation(group)
          }
        } else if (this.userRegistrationStatus.registered) {
          this.changeGroup(group)
        } else if (this.availableSpots == 0) {
          this.$store.dispatch('showMessage', {
            show: true,
            message: 'Unable to register. Max spots reached.',
            type: 'error',
          })
        }
      }
    },
    emptySpotsInGroup(group) {
      let groupReservations = this.groupRegistrationReservations.filter(
        (reservation) => reservation.group.id == group.id
      )
      if (
        this.eventData.groupSize -
          group.playerConnectionsV2.length -
          groupReservations.length <=
        0
      ) {
        return 0
      } else {
        return (
          this.eventData.groupSize -
          group.playerConnectionsV2.length -
          groupReservations.length
        )
      }
    },
    async loadPool(poolId) {
      try {
        const poolData = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
          {
            pool(poolId:"${poolId}"){
              id
              status
              areGroupsPublic
              groups{
                id
                startsAt
                position
                status
                startHole{
                  id
                  number
                  name
                }
                pool{
                  id
                }
                playerConnectionsV2{
                  ... on GroupTeamPlayerConnection{
                    id
                    position
                    teamPlayer{
                      id
                      team{
                        id
                        name
                        teamPlayers{
                          id
                          user{
                            id
                            firstName
                            lastName
                            profile{
                              profileImageUrl
                            }
                          }
                        }
                      }
                      divisionId
                      division{
                        type
                      }
                    }
                  }
                  ... on GroupPlayerConnection{
                    id
                    position
                    player{
                      id
                      userId
                      user{
                        firstName
                        lastName
                        profile{
                          profileImageUrl
                        }
                      }
                      division{
                        id
                        type
                      }
                    }
                  }
                }
              }
            }
          }
          `,
          },
        })

        this.poolData = poolData.data.data.pool.areGroupsPublic
          ? poolData.data.data.pool
          : null
        this.loaded = true
        this.$emit('contentLoaded')
      } catch (err) {
        console.log(err)
      }
    },
    async loadData() {
      if (this.isUserLoggedIn === false) {
        this.$store.dispatch('setReturnToRouteData', this.$route)
        this.$router.push({ name: 'login' })
      }
      try {
        const groupData = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
          {
            me {
              teams{
                id
              }
            }
            event(eventId:"${this.eventId}"){
              id
              singles
              scoringAs
              defaultToTeamName
              status
              isRegistrationOpen
              maxSpots
              groupSize
              playersCount
              registrationMethod
              minGroupStartSize
              userEventMeta {
                ... on EventTeamMeta {
                  favourite {
                    id
                    createdAt
                  }
                  registrations {
                    id
                    status
                    player{
                      id
                    }
                  }
                }
                ... on EventUserMeta {
                  registration {
                    id
                    status
                    player{
                      id
                    }
                  }
                  favourite {
                    id
                    createdAt
                  }
                }
              } 
              tour{
                allowRegistrationMessage
                id
              }
              players{
                userId
              }
              registrations{
                ... on TeamRegistration {
                  id
                  status
                  team{
                    id
                  }
                }
                ... on Registration {
                  id
                  status
                  user{
                    id
                  }
                }
              }
              rounds{
                id
                pools{
                  id
                  name
                  areGroupsPublic
                }
              }
              groupRegistrationReservations{
                id
                expireAt
                group{
                  id
                  status
                  position
                }
                user{
                  id
                  firstName
                  lastName
                  profile{
                    profileImageUrl
                  }
                }
              }
            }
          }
          `,
          },
        })

        this.eventData = groupData.data.data.event
        this.myTeams = groupData.data.data.me.teams ?? []
        this.players = groupData.data.data.event.players ?? []
        this.teams = groupData.data.data.event.teams ?? []
        this.userRegistration =
          groupData.data.data.event.userEventMeta.registration ||
          groupData.data.data.event.userEventMeta.registrations
        this.groupRegistrationReservations =
          groupData.data.data.event.groupRegistrationReservations
        this.clickedGroup = null

        const round = this.eventData.rounds.find((r) => r.id === this.roundId)
        const poolToLoad = round.pools.find((p) => p.areGroupsPublic)
        this.loadPool(poolToLoad.id)

        return groupData
      } catch (err) {
        console.log(err)
      }
    },
  },
  async beforeMount() {
    await this.loadData()
  },
  beforeCreate() {},
  destroyed() {},
}
</script>
<style lang="scss" scoped>
@import '@/styles/Typography';

.min-groupsize-info-banner {
  margin-top: 24px;
  display: grid;
  grid-template-columns: 14px 1fr;
  border-radius: 5px;
  background: $mist;
  padding: 12px;
  gap: 12px;

  svg {
    margin-top: 2px;
  }

  p {
    font-size: 14px;
    line-height: 20px;
  }
}

.spots-label {
  font-size: 14px;
  line-height: 14px;
  background: #ccf5eb;
  color: $grass;
  border-radius: 4px;
  padding: 5px 8px;

  &.none {
    background: #ffcce0;
    color: $dusk;
  }

  &.few {
    background: #ffe2da;
    color: $sunset;
  }
}

.registration-info {
  font-size: 14px;
  margin-top: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.switch-copy {
  margin-top: 24px;
  font-size: 16px;
}

.round-nav {
  margin-top: 24px;
  font-size: 16px;

  &.pools {
    margin-bottom: -4px;
  }
}

.pool-nav {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 8px;
  margin-top: 16px;

  p {
    user-select: none;
    cursor: pointer;
    font-size: 16px;
    padding: 2px 12px;
    background: $mist;
    line-height: 24px;
    border-radius: 14px;
    transition: all ease 0.3s;

    &.disabled {
      color: $blizzard;

      &:hover {
        background: $mist;
        cursor: default;
      }
    }

    &.active {
      background: $midnight;
      color: white;
      cursor: default;

      &:hover {
        background: $midnight;
      }
    }

    &:hover {
      background: $fog;
    }
  }
}

.reg-questions-container {
  padding: 24px;

  h6 {
    font-size: 18px;
  }

  .question {
    margin-top: 18px;
  }

  textarea {
    width: 100%;
    padding: 12px;
    margin-top: 8px;
    border: 1px solid $sleet;
    border-radius: 5px;
    font-size: 16px;
  }

  .question-options-wrapper {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .question-option {
      display: flex;
      align-items: center;
      gap: 15px;
    }
  }
}

.change-group-container {
  padding: 24px;
  text-align: center;

  .change-group-title {
    font-size: 24px;
  }
  p {
    margin-top: 16px;
    font-size: 16px;
  }
}

.action-buttons {
  margin-top: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.pool-title {
  font-size: 20px;
  grid-column: 1/-1;
  background: $mist;
  border-radius: 5px;
  padding: 8px 12px;
}

.group-signup-container {
  max-width: 1050px;
  padding: 0 24px 24px 24px;
}

.headline {
  h3 {
    font-size: 20px;
    padding-bottom: 8px;
    border-bottom: 1px solid $midnight;
  }

  p {
    margin-top: 24px;
    font-size: 16px;
  }
}

.reserved-spot-banner {
  margin-top: 16px;
  background: #fff4d9;
  display: flex;
  padding: 12px 15px;
  gap: 12px;
  border-radius: 5px;

  p {
    font-size: 1rem;
  }

  svg {
    min-width: 20px;
    height: 20px;
  }
}

.doubles {
  .player {
    min-height: 118px;
  }
}

.reserved {
  .left {
    opacity: 0.5;
  }
}

.pool {
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.team-wrapper {
  flex-direction: column;
  display: flex;
  gap: 12px;
}

.team-player,
.player-wrapper {
  gap: 12px;
  display: flex;
  max-width: 100%;

  .player-name {
    gap: 2px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .first {
    font-size: 20px;
    line-height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .last {
    font-size: 16px;
    line-height: 16px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.player {
  justify-content: space-between;
  background: $mist;
  border: 1px solid $mist;
  padding: 12px;
  display: flex;
  flex-shrink: 0;
  border-radius: 8px;
  gap: 12px;
  min-height: 66px;

  &.available {
    background: none;
    border: 1px solid $strom;
    cursor: pointer;
    transition: all ease 0.3s;
    display: flex;
    align-items: center;
    padding: 20px;

    &:hover {
      background: $mist;
      border: 1px solid $midnight;
    }
  }

  &.disabled {
    background: none;
    border: 1px solid $sleet;
  }
}

.left {
  overflow: hidden;
}

.right {
  .division-label,
  .reserved-label {
    letter-spacing: 0.12px;
    font-size: 12px;
    text-transform: uppercase;
    background: $strom;
    color: white;
    border-radius: 2px;
    padding: 1px 3px;
  }

  .reserved-label {
    background: $sunrise;
  }
}

.group-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: hidden;
  min-width: 250px;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 12px;
  background: $midnight;
  border-radius: 8px;
  height: 40px;

  &.live {
    background: $grass;
  }

  span {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  p {
    font-size: 22px;
    color: white;
  }

  svg {
    width: 20px;
    height: 20px;
  }
}

@media (min-width: 480px) and (orientation: landscape) {
  .pool {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 40px 32px;
  }
}

@media (min-width: 544px) {
}

@media (min-width: 768px) {
}

@media (min-width: 1200px) {
  .min-groupsize-info-banner {
    margin-top: 32px;

    padding: 16px;

    svg {
      margin-top: 2px;
    }

    p {
      font-size: 16px;
      line-height: 24px;
    }
  }

  .round-nav {
    font-size: 18px;
    margin-top: 32px;

    &.pools {
      margin-bottom: -16px;
    }
  }

  .spots-label {
    font-size: 16px;
    line-height: 16px;
    background: #ccf5eb;
    color: $grass;
    border-radius: 6px;
    padding: 8px 12px;
  }

  .registration-info {
    font-size: 18px;
    margin-top: 32px;
    flex-direction: row;
    align-items: center;
  }

  .switch-copy {
    margin-top: 32px;
  }

  .group-signup-container {
    padding: 0 64px 64px 64px;
  }

  .reserved-spot-banner {
    margin-top: 24px;
  }

  .pool-nav {
    margin-top: 32px;
  }

  .headline {
    h3 {
      font-size: 24px;
      padding-bottom: 12px;
    }

    p {
      margin-top: 32px;
      font-size: 18px;
    }
  }

  .pool {
    margin-top: 40px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 40px 32px;
  }
}
</style>
